import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Globe, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/utils/apiErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CRISANGEL_TZ_OFFSET_MIN = -7 * 60; // GMT-7
const CRISANGEL_TZ_LABEL = "GMT-7 (Hermosillo)";

const TWO = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${TWO(d.getMonth() + 1)}-${TWO(d.getDate())}`;
const monthLabel = (d) =>
  d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });

/** Translate a CrisAngel-local "YYYY-MM-DD HH:MM" pair into the client's
 * local equivalent and return both strings + the hour difference. */
const convertSlotToClient = (date, time) => {
  const [Y, M, D] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  // Treat the slot as if it were UTC, then shift to CrisAngel's local time.
  // i.e. real UTC = local CrisAngel time - tz_offset_min
  const utcMs = Date.UTC(Y, M - 1, D, hh, mm) - CRISANGEL_TZ_OFFSET_MIN * 60_000;
  const clientDate = new Date(utcMs);
  return clientDate;
};

const fmtClientTime = (date) =>
  date.toLocaleString("es-MX", {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  });

const fmtClientTimeShort = (date) =>
  date.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

/** Hour difference (client minus CrisAngel) — negative means client is
 * behind CrisAngel, positive means ahead. */
const clientTzDiffHours = () => {
  // getTimezoneOffset returns minutes WEST of UTC, so a positive value means
  // the client is behind UTC. CrisAngel is at GMT-7 → +420 min west.
  const clientWestMin = new Date().getTimezoneOffset();
  const crisangelWestMin = -CRISANGEL_TZ_OFFSET_MIN; // 420
  const diffMin = crisangelWestMin - clientWestMin; // east is positive
  return diffMin / 60;
};

export const AppointmentBookingModal = ({ plan, open, onClose, onBooked }) => {
  const { authHeader } = useAuth();
  const [today] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedTime, setSelectedTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [busySlots, setBusySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [crisangelNow, setCrisangelNow] = useState(null);

  // Live CrisAngel clock so the client can see Crisangel's local time.
  useEffect(() => {
    if (!open) return undefined;
    const tick = () => {
      const utc = Date.now();
      setCrisangelNow(new Date(utc + CRISANGEL_TZ_OFFSET_MIN * 60_000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open]);

  // Generate the next 30 days for the date picker
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 30; i += 1) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [today]);

  // Fetch slot availability when date changes
  useEffect(() => {
    if (!open || !selectedDate) return undefined;
    let cancelled = false;
    setLoadingSlots(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/users/appointments/available-slots?date=${ymd(selectedDate)}`,
          { headers: authHeader() },
        );
        if (!cancelled) {
          setAllSlots(r.data.all_slots || []);
          setBusySlots(r.data.busy || []);
        }
      } catch {
        if (!cancelled) {
          setAllSlots([]);
          setBusySlots([]);
        }
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedDate, authHeader]);

  const tzDiff = clientTzDiffHours();
  const tzDiffLabel = useMemo(() => {
    if (tzDiff === 0) return "Estás en la misma zona horaria que CrisAngel.";
    const abs = Math.abs(tzDiff);
    const hours = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1);
    const dir = tzDiff > 0 ? "adelantado/a" : "atrasado/a";
    return `Estás ${hours} h ${dir} respecto a CrisAngel Astral.`;
  }, [tzDiff]);

  const isPastSlot = (date, time) => {
    // Compare CrisAngel-local slot vs CrisAngel-local now
    if (!crisangelNow) return false;
    const slotLocal = new Date(
      date.getFullYear(), date.getMonth(), date.getDate(),
      Number(time.slice(0, 2)), Number(time.slice(3, 5)), 0,
    );
    return slotLocal.getTime() <=
      new Date(
        crisangelNow.getFullYear(), crisangelNow.getMonth(), crisangelNow.getDate(),
        crisangelNow.getHours(), crisangelNow.getMinutes(), 0,
      ).getTime();
  };

  const reset = () => {
    setSelectedTime("");
    setConfirming(false);
  };

  const confirm = async () => {
    if (!selectedTime) { toast.error("Elige un horario disponible"); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/users/appointments/book`,
        {
          plan_id: plan._id || plan.id,
          scheduled_date: ymd(selectedDate),
          scheduled_time: selectedTime,
          notes: "",
        },
        { headers: authHeader() },
      );
      const localTime = convertSlotToClient(ymd(selectedDate), selectedTime);
      toast.success(
        `🎉 Cita confirmada · ${ymd(selectedDate)} ${selectedTime} (CrisAngel) · ${fmtClientTime(localTime)} (tu hora)`,
        { duration: 8000 },
      );
      onBooked?.(r.data.appointment);
      onClose?.();
      reset();
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo agendar la cita"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose?.(); reset(); } }}>
      <DialogContent
        data-testid="appointment-booking-modal"
        className="bg-gradient-to-b from-[#171029] to-black border border-[#d4af37]/30 text-white w-[96vw] max-w-2xl max-h-[95vh] overflow-y-auto p-4 sm:p-6 rounded-xl"
      >
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2 text-[#d4af37]">
            <CalendarDays className="w-5 h-5" />
            Agendar mi cita
          </DialogTitle>
        </DialogHeader>

        {/* Plan summary + live CrisAngel clock */}
        <div className="rounded-xl border border-white/10 bg-black/40 p-3 mb-3">
          <div className="text-xs text-neutral-400 uppercase tracking-widest mb-0.5">Servicio liquidado</div>
          <div className="text-sm text-[#d4af37] font-semibold">{plan?.service_name || "Servicio"}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-neutral-300">
            <span className="inline-flex items-center gap-1">
              <Globe className="w-3 h-3 text-[#d4af37]" />
              Hora CrisAngel:{" "}
              <span className="text-[#d4af37] font-mono">
                {crisangelNow
                  ? `${TWO(crisangelNow.getHours())}:${TWO(crisangelNow.getMinutes())} (${CRISANGEL_TZ_LABEL})`
                  : "..."}
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3 text-emerald-400" />
              {tzDiffLabel}
            </span>
          </div>
          <div className="mt-1 text-[10px] text-neutral-500">
            Atención de 09:00 a 22:00 todos los días (hora local de CrisAngel Astral).
          </div>
        </div>

        {/* Date picker (horizontal scroll, 30 days) */}
        <div className="mb-3">
          <div className="text-[11px] text-neutral-400 uppercase tracking-widest mb-1.5">
            Elige el día — <span className="text-neutral-300">{monthLabel(selectedDate)}</span>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {days.map((d) => {
              const active = ymd(d) === ymd(selectedDate);
              const isToday = ymd(d) === ymd(today);
              return (
                <button
                  key={ymd(d)}
                  type="button"
                  data-testid={`booking-day-${ymd(d)}`}
                  onClick={() => { setSelectedDate(d); setSelectedTime(""); }}
                  className={`shrink-0 snap-start min-w-[58px] px-2 py-2 rounded-lg border text-center transition ${
                    active
                      ? "bg-[#d4af37] text-black border-[#d4af37]"
                      : "border-white/10 text-neutral-200 hover:border-[#d4af37]/40 hover:bg-white/5"
                  }`}
                >
                  <div className="text-[9px] uppercase opacity-80">
                    {d.toLocaleDateString("es-MX", { weekday: "short" })}
                  </div>
                  <div className="text-lg font-serif font-bold leading-none">{d.getDate()}</div>
                  {isToday && <div className="text-[8px] mt-0.5 opacity-80">hoy</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Slots */}
        <div className="mb-3">
          <div className="text-[11px] text-neutral-400 uppercase tracking-widest mb-1.5 flex items-center gap-2">
            Horarios disponibles
            <span className="text-emerald-400 normal-case">
              {ymd(selectedDate)}
            </span>
            {loadingSlots && <Loader2 className="w-3 h-3 animate-spin" />}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
            {allSlots.map((slot) => {
              const isBusy = busySlots.includes(slot);
              const past = isPastSlot(selectedDate, slot);
              const disabled = isBusy || past;
              const active = selectedTime === slot;
              const localTime = convertSlotToClient(ymd(selectedDate), slot);
              return (
                <button
                  key={slot}
                  type="button"
                  data-testid={`booking-slot-${slot}`}
                  disabled={disabled}
                  onClick={() => setSelectedTime(slot)}
                  className={`px-1 py-2 rounded-lg border text-center transition disabled:opacity-30 disabled:cursor-not-allowed ${
                    active
                      ? "bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37] ring-2 ring-[#d4af37]/40"
                      : disabled
                        ? "border-white/5 bg-black/20 text-neutral-500 line-through"
                        : "border-white/10 hover:border-[#d4af37]/40 text-neutral-100 active:scale-95"
                  }`}
                >
                  <div className="text-[13px] sm:text-sm font-mono font-bold leading-tight">{slot}</div>
                  <div className="text-[9px] sm:text-[10px] text-emerald-400 mt-0.5 font-mono leading-tight whitespace-nowrap">
                    {fmtClientTimeShort(localTime)}
                  </div>
                  {isBusy && <div className="text-[8px] text-red-400 leading-tight">ocup.</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selection summary */}
        {selectedTime && (
          <div
            data-testid="booking-summary"
            className="rounded-xl border border-[#d4af37]/40 bg-[#d4af37]/5 p-3 mb-3"
          >
            <div className="text-[11px] text-[#d4af37] uppercase tracking-widest mb-1">Tu cita quedará así</div>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-[10px] text-neutral-400 uppercase">Hora CrisAngel ({CRISANGEL_TZ_LABEL})</div>
                <div className="text-[#d4af37] font-mono">
                  {ymd(selectedDate)} · {selectedTime}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-neutral-400 uppercase">Tu hora local</div>
                <div className="text-emerald-400 font-mono">
                  {fmtClientTime(convertSlotToClient(ymd(selectedDate), selectedTime))}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-neutral-400 mt-2">{tzDiffLabel}</div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={() => { onClose?.(); reset(); }}
            className="text-xs px-3 py-2 rounded border border-white/10 text-neutral-300 hover:bg-white/5 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Cancelar
          </button>
          <button
            type="button"
            data-testid="booking-confirm"
            disabled={!selectedTime || busy}
            onClick={confirm}
            className="text-xs px-5 py-2 rounded-full bg-[#d4af37] hover:bg-[#e6c350] text-black font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Confirmar cita
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Wrapper used in the user account page: shows the "Agendar mi cita" button
// for each fully-paid plan that does NOT already have an appointment.
export const BookableAppointmentsList = ({ plans, onBooked }) => {
  const [bookingPlan, setBookingPlan] = useState(null);
  const eligible = (plans || []).filter(
    (p) => (p.fully_paid || p.status === "paid") && !p.appointment_id,
  );
  if (eligible.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="bookable-plans">
      {eligible.map((p) => (
        <motion.div
          key={p._id || p.id}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center justify-between gap-3"
        >
          <div>
            <div className="text-[10px] text-emerald-400 uppercase tracking-widest">Servicio liquidado</div>
            <div className="text-sm text-white font-semibold">{p.service_name}</div>
            <div className="text-[10px] text-neutral-400">¡Puedes agendar tu cita ahora!</div>
          </div>
          <button
            type="button"
            data-testid={`book-plan-${p._id || p.id}`}
            onClick={() => setBookingPlan(p)}
            className="shrink-0 px-3 py-2 rounded-full bg-[#d4af37] hover:bg-[#e6c350] text-black text-xs font-semibold inline-flex items-center gap-1"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Agendar mi cita
          </button>
        </motion.div>
      ))}
      <AnimatePresence>
        {bookingPlan && (
          <AppointmentBookingModal
            plan={bookingPlan}
            open={!!bookingPlan}
            onClose={() => setBookingPlan(null)}
            onBooked={(apt) => { setBookingPlan(null); onBooked?.(apt); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
